import React from "react";
import { ArrowRight, Star, ShieldCheck } from "lucide-react";
import { Button } from "../ui/button";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/1d8d6f75-1116-44e7-832c-39bdf552935e/images/917caad562c4ae1cf74e6ea158b01c3b73b2097017243b563a288ae983642b50.png";

export const Hero = () => {
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Lush English garden in morning light"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-sand via-brand-sand/85 to-brand-sand/10" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-brand-sand" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 pt-20 pb-32 lg:pt-32 lg:pb-44 min-h-[88vh] flex items-center">
        <div className="max-w-2xl">
          <span
            data-testid="hero-overline"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-brand-terracotta mb-6 fade-up"
          >
            <span className="w-8 h-[2px] bg-brand-terracotta" />
            Buckinghamshire & Hertfordshire · Fully Insured
          </span>

          <h1
            data-testid="hero-heading"
            className="font-display font-bold text-brand-moss-deep text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight fade-up delay-100"
          >
            Friendly gardeners <br />
            for your <span className="italic text-brand-moss">happiest</span> garden yet.
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-7 text-lg sm:text-xl text-brand-moss-deep/75 leading-relaxed max-w-xl fade-up delay-200"
          >
            From a tidy lawn to a full landscape rebuild, Rafael Costa Garden Care looks after gardens
            across the area with honest pricing and a proper cuppa-friendly attitude.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4 fade-up delay-300">
            <Button
              asChild
              data-testid="hero-primary-cta"
              className="rounded-full bg-brand-moss hover:bg-brand-moss-dark text-white px-8 h-14 text-base font-semibold shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <a href="#contact" className="flex items-center gap-2">
                Get a free quote <ArrowRight className="w-4 h-4" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="hero-secondary-cta"
              className="rounded-full bg-white/80 hover:bg-white border-brand-moss/30 text-brand-moss-deep px-8 h-14 text-base font-semibold"
            >
              <a href="#services">See our services</a>
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-brand-moss-deep/70 fade-up delay-400">
            <div className="flex items-center gap-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-brand-terracotta text-brand-terracotta" />
                ))}
              </div>
              <span className="font-semibold text-brand-moss-deep">5.0</span>
              <span>· Loved by local homeowners</span>
            </div>
            <a
              href="/docs/insurance-certificate.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-insurance-link"
              className="hidden sm:flex items-center gap-2 hover:text-brand-moss transition-colors"
            >
              <ShieldCheck className="w-4 h-4 text-brand-moss" />
              <span className="underline-offset-4 hover:underline">£1m Public liability insured</span>
            </a>
          </div>
        </div>
      </div>

      {/* Decorative bottom strip */}
      <div className="relative h-2 bg-gradient-to-r from-transparent via-brand-leaf/30 to-transparent" />
    </section>
  );
};

export default Hero;
